import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchClientPurchase,
  fetchClientPurchases,
  fetchPurchaseEvents,
  recordPurchasePaid,
} from "@/api/purchases";
import type { ClientPurchaseListQuery, RecordPurchasePaidPayload } from "@/interfaces/purchases";

export function useClientPurchases(
  query: ClientPurchaseListQuery = {},
  // Options rather than part of `query`, which is the cache key and the request params.
  opts: { enabled?: boolean; staleTime?: number } = {},
) {
  return useQuery({
    queryKey: ["clientPurchases", query],
    queryFn: () => fetchClientPurchases(query),
    enabled: opts.enabled ?? true,
    staleTime: opts.staleTime,
  });
}

export function useClientPurchase(id: number | null) {
  return useQuery({
    queryKey: ["clientPurchase", id],
    queryFn: () => fetchClientPurchase(id as number),
    enabled: id != null,
  });
}

/** Refreshes both the blotter and the open detail, since the derived status changes. */
export function useRecordPurchasePaid(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RecordPurchasePaidPayload) => recordPurchasePaid(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clientPurchases"] });
      qc.invalidateQueries({ queryKey: ["clientPurchase", id] });
      qc.invalidateQueries({ queryKey: ["journalEntries"] });
    },
  });
}

/** A purchase's activity feed. */
export function usePurchaseEvents(purchaseId: number | null) {
  return useQuery({
    queryKey: ["purchaseEvents", purchaseId],
    queryFn: () => fetchPurchaseEvents(purchaseId as number),
    enabled: purchaseId != null,
  });
}
